import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { ListChecks, Plus, Search, MoreHorizontal, Pencil, Trash2, DollarSign, Tag, Copy } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['all', 'service', 'material', 'permit', 'labor', 'equipment'];
const CATEGORY_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  material: 'bg-emerald-100 text-emerald-700',
  permit: 'bg-purple-100 text-purple-700',
  labor: 'bg-amber-100 text-amber-700',
  equipment: 'bg-slate-100 text-slate-700'
};

export default function LineItemsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', sku: '', supplier_sku: '', unit: 'unit', unit_cost: 0, unit_price: 0, default_quantity: 1, category: 'service', is_taxable: false, notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (search) params.search = search;
      const res = await api.get('/line-items', { params });
      setItems(res.data);
    } catch { toast.error(t('lineItems.errorLoading')); }
    finally { setLoading(false); }
  }, [categoryFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const emptyForm = () => ({ name: '', description: '', sku: '', supplier_sku: '', unit: 'unit', unit_cost: 0, unit_price: 0, default_quantity: 1, category: 'service', is_taxable: false, notes: '' });

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name || '', description: item.description || '', sku: item.sku || '', supplier_sku: item.supplier_sku || '', unit: item.unit || 'unit', unit_cost: item.unit_cost || 0, unit_price: item.unit_price || 0, default_quantity: item.default_quantity || 1, category: item.category || 'service', is_taxable: item.is_taxable || false, notes: item.notes || '' });
    setDialogOpen(true);
  };

  const duplicateItem = (item) => {
    setEditing(null);
    setForm({ name: `${item.name} ${t('lineItems.copyLabel')}`, description: item.description || '', sku: '', supplier_sku: item.supplier_sku || '', unit: item.unit || 'unit', unit_cost: item.unit_cost || 0, unit_price: item.unit_price || 0, default_quantity: item.default_quantity || 1, category: item.category || 'service', is_taxable: item.is_taxable || false, notes: item.notes || '' });
    setDialogOpen(true);
    toast.info(t('lineItems.duplicating'));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('lineItems.nameRequired')); return; }
    try {
      if (editing) { await api.put(`/line-items/${editing.id}`, form); toast.success(t('lineItems.updated')); }
      else { await api.post('/line-items', form); toast.success(t('lineItems.created')); }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('lineItems.errorSaving')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/line-items/${id}`); toast.success(t('lineItems.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const calcMargin = () => {
    const margin = form.unit_price - form.unit_cost;
    const percent = form.unit_cost > 0 ? ((margin / form.unit_cost) * 100).toFixed(1) : 0;
    return { margin, percent };
  };
  const { margin, percent } = calcMargin();

  return (
    <div data-testid="line-items-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <ListChecks className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('lineItems.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('lineItems.totalItems', { count: items.length })}</p>
        </div>
        <Button data-testid="new-line-item-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> {t('lineItems.newItem')}
        </Button>
      </div>

      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">{t('lineItems.infoBanner')}</p>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-line-items" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('lineItems.searchPlaceholder')} className="pl-10 h-9 bg-white" />
        </div>
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="w-auto">
          <TabsList className="bg-white border border-gray-200 h-9">
            {CATEGORIES.map(c => (
              <TabsTrigger key={c} value={c} data-testid={`category-filter-${c}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                {t(`lineItems.filters.${c}`, { defaultValue: c })}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <ListChecks className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('lineItems.noItems')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('lineItems.createFirstItem')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">SKU</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('lineItems.name')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('lineItems.category')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('lineItems.costPrice')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('lineItems.salePrice')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('lineItems.marginLabel')}</th>
                    <th className="text-center text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden xl:table-cell">{t('lineItems.defaultQuantity')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} data-testid={`line-item-row-${item.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => openEdit(item)}>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-mono font-medium text-[#003366]">{item.sku}</p>
                        {item.supplier_sku && <p className="text-xs text-gray-400 font-mono">{item.supplier_sku}</p>}
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        {item.description && <p className="text-xs text-gray-400 line-clamp-1">{item.description}</p>}
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600'}`}>
                          {t(`lineItems.filters.${item.category}`, { defaultValue: item.category })}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right text-sm text-gray-600">${(item.unit_cost || 0).toFixed(2)}</td>
                      <td className="px-5 py-3.5 text-right text-sm font-semibold text-gray-700">${(item.unit_price || 0).toFixed(2)}</td>
                      <td className="px-5 py-3.5 text-right hidden lg:table-cell">
                        <span className={`text-sm font-medium ${(item.margin || 0) > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>${(item.margin || 0).toFixed(2)}</span>
                        {item.margin_percent > 0 && <span className="text-xs text-gray-400 ml-1">({item.margin_percent}%)</span>}
                      </td>
                      <td className="px-5 py-3.5 text-center text-sm text-gray-600 hidden xl:table-cell">{item.default_quantity} {item.unit}</td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`line-item-actions-${item.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicateItem(item)}><Copy className="w-3.5 h-3.5 mr-2" />{t('common.duplicate')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <ListChecks className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('lineItems.noItems')}</p>
          </CardContent></Card>
        ) : (
          items.map((item, idx) => (
            <Card key={item.id} data-testid={`line-item-mobile-${item.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter cursor-pointer" style={{ animationDelay: `${idx * 50}ms` }} onClick={() => openEdit(item)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-[#003366]">{item.sku}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600'}`}>{t(`lineItems.filters.${item.category}`, { defaultValue: item.category })}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{item.name}</p>
                  </div>
                  <div onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicateItem(item)}><Copy className="w-3.5 h-3.5 mr-2" />{t('common.duplicate')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{t('lineItems.costPrice')}: <span className="font-medium">${(item.unit_cost || 0).toFixed(2)}</span></span>
                    <span className="text-xs text-gray-500">{t('lineItems.salePrice')}: <span className="font-semibold text-gray-800">${(item.unit_price || 0).toFixed(2)}</span></span>
                  </div>
                  {item.margin > 0 && <span className="text-xs text-emerald-600 font-medium">+${item.margin.toFixed(2)}</span>}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#3399FF]" />
              {editing ? t('lineItems.editItem') : t('lineItems.newItem')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('lineItems.name')} *</Label>
                <Input data-testid="line-item-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('lineItems.itemNamePlaceholder')} />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('lineItems.description')}</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={t('lineItems.descriptionPlaceholder')} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>{t('lineItems.sku')}</Label>
                <Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder={t('lineItems.skuPlaceholder')} />
                <p className="text-xs text-gray-400">{t('lineItems.skuHint')}</p>
              </div>
              <div className="space-y-2">
                <Label>{t('lineItems.supplierSku')}</Label>
                <Input value={form.supplier_sku} onChange={e => setForm({ ...form, supplier_sku: e.target.value })} placeholder="Ej: 4337-101" />
                <p className="text-xs text-gray-400">{t('lineItems.supplierSkuHint')}</p>
              </div>
              <div className="space-y-2">
                <Label>{t('lineItems.category')}</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="line-item-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c !== 'all').map(c => <SelectItem key={c} value={c}>{t(`lineItems.filters.${c}`, { defaultValue: c })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('lineItems.unit')}</Label>
                <Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="unit, ft, hour, gal..." />
              </div>
            </div>

            <div className="p-4 rounded-lg bg-gray-50 border border-gray-100 space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><DollarSign className="w-4 h-4" /> {t('lineItems.pricesSection')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('lineItems.unitCostLabel')}</Label>
                  <Input type="number" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
                  <p className="text-xs text-gray-400">{t('lineItems.unitCostHint')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('lineItems.unitPriceLabel')}</Label>
                  <Input data-testid="line-item-price-input" type="number" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
                  <p className="text-xs text-gray-400">{t('lineItems.unitPriceHint')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('lineItems.defaultQtyLabel')}</Label>
                  <Input type="number" value={form.default_quantity} onChange={e => setForm({ ...form, default_quantity: parseFloat(e.target.value) || 1 })} min="0" step="1" />
                  <p className="text-xs text-gray-400">{t('lineItems.defaultQtyHint')}</p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('lineItems.calculatedMargin')}</span>
                <div className="text-right">
                  <span className={`text-lg font-bold ${margin > 0 ? 'text-emerald-600' : margin < 0 ? 'text-red-500' : 'text-gray-400'}`}>${margin.toFixed(2)}</span>
                  {form.unit_cost > 0 && <span className="text-sm text-gray-400 ml-2">({percent}%)</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <Checkbox id="is_taxable" checked={form.is_taxable} onCheckedChange={v => setForm({ ...form, is_taxable: v })} />
              <Label htmlFor="is_taxable" className="text-sm cursor-pointer">{t('lineItems.taxableLabel')}</Label>
            </div>

            <div className="space-y-2">
              <Label>{t('lineItems.internalNotes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder={t('lineItems.internalNotesPlaceholder')} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-line-item-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('lineItems.saveChanges') : t('lineItems.createItem')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
